import React, { useEffect, useState, useRef } from 'react';
import { ScaleLoader } from 'react-spinners';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Application } from '../core/Application';
import { SceneManager } from '../core/SceneManager';
import { InputManager } from '../core/InputManager';
import { Camera } from '../core/Camera';
import { assetLoader } from '../core/AssetLoader';
import { audioManager } from '../audio/AudioManager';
import { onGameEvent, emitGameEvent } from '../core/GameEvents';
import { networkManager } from '../systems/NetworkManager';

// Scenes
import { BootScene } from '../scenes/BootScene';
import { GameScene } from '../scenes/GameScene';

// Components
import { GameHUD } from './hud/GameHUD';
import { TutorialTransmissionPanel } from './hud/TutorialTransmissionPanel';
import { WelcomeTransmissionPanel } from './hud/WelcomeTransmissionPanel';
import { DeathModal } from './hud/DeathModal';
import { LoginForm } from './auth/LoginForm';
import { StationUI } from './station';
import { ChatPanel, ShipInventoryModal, BountyBoardModal } from './panels';
import { ConstellationMapOverlay, UniverseMapOverlay } from './maps';
import { PlanetPanel } from './planet/PlanetPanel';

type AppState = 'login' | 'loading' | 'playing';

// Style for the game container
const containerStyle: React.CSSProperties = {
  position: 'relative',
  width: '100vw',
  height: '100vh',
  overflow: 'hidden',
  backgroundColor: '#0f1b26',
};

const canvasContainerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
};

const uiOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
};

// Volume slider component for settings
function VolumeSlider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  const sliderContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '10px 0',
    padding: '16px 20px',
    backgroundColor: '#1a2a38',
    border: '2px solid #4a5a6a',
    borderRadius: '4px',
    width: '300px',
  };

  const sliderStyle: React.CSSProperties = {
    width: '100%',
    height: '8px',
    marginTop: '12px',
    cursor: 'pointer',
    appearance: 'none',
    backgroundColor: '#0f1b26',
    borderRadius: '4px',
    outline: 'none',
  };

  return (
    <div style={sliderContainerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <span>{label}</span>
        <span style={{ color: '#20a5a6' }}>{Math.round(value * 100)}%</span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={sliderStyle}
      />
    </div>
  );
}

// Settings screen component
function SettingsScreen({ onBack }: { onBack: () => void }) {
  const [masterVolume, setMasterVolumeState] = useState(audioManager.getMasterVolume());
  const [musicVolume, setMusicVolumeState] = useState(audioManager.getMusicVolume());
  const [sfxVolume, setSfxVolumeState] = useState(audioManager.getSfxVolume());

  const handleMasterChange = (value: number) => {
    setMasterVolumeState(value);
    audioManager.setMasterVolume(value);
  };

  const handleMusicChange = (value: number) => {
    setMusicVolumeState(value);
    audioManager.setMusicVolume(value);
  };

  const handleSfxChange = (value: number) => {
    setSfxVolumeState(value);
    audioManager.setSfxVolume(value);
  };

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(15, 27, 38, 0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    fontFamily: "'JetBrains Mono', monospace",
    color: '#f5e8d1',
    pointerEvents: 'auto',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 40px',
    margin: '8px 0',
    backgroundColor: '#1a2a38',
    color: '#f5e8d1',
    border: '2px solid #4a5a6a',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace",
    width: '200px',
    transition: 'all 0.2s',
  };

  return (
    <div style={menuStyle}>
      <h1 style={{ color: '#20a5a6', marginBottom: 30, fontSize: 32 }}>Settings</h1>

      <VolumeSlider label="Master Volume" value={masterVolume} onChange={handleMasterChange} />
      <VolumeSlider label="Music Volume" value={musicVolume} onChange={handleMusicChange} />
      <VolumeSlider label="SFX Volume" value={sfxVolume} onChange={handleSfxChange} />

      <button style={{ ...buttonStyle, marginTop: 20 }} onClick={onBack}>
        Back
      </button>
      <p style={{ marginTop: 30, color: '#4a5a6a', fontSize: 11 }}>Press ESC to go back</p>
    </div>
  );
}

// Main menu component (ESC menu)
function MainMenu({ onClose, onSettings, onQuit }: { onClose: () => void; onSettings: () => void; onQuit: () => void }) {
  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(15, 27, 38, 0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    fontFamily: "'JetBrains Mono', monospace",
    color: '#f5e8d1',
    pointerEvents: 'auto',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 40px',
    margin: '8px 0',
    backgroundColor: '#1a2a38',
    color: '#f5e8d1',
    border: '2px solid #4a5a6a',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace",
    width: '200px',
    transition: 'all 0.2s',
  };

  return (
    <div style={menuStyle}>
      <h1 style={{ color: '#20a5a6', marginBottom: 40, fontSize: 32 }}>ASTRA</h1>
      <button style={buttonStyle} onClick={onClose}>
        Resume Game
      </button>
      <button style={buttonStyle} onClick={onSettings}>
        Settings
      </button>
      <button style={{ ...buttonStyle, borderColor: '#dd5639', color: '#dd5639' }} onClick={onQuit}>
        Quit to Login
      </button>
      <p style={{ marginTop: 40, color: '#4a5a6a', fontSize: 11 }}>Press ESC to close</p>
    </div>
  );
}

// Loading screen component — matches LoginForm palette & starfield
const loadingStars = Array.from({ length: 100 }, (_, i) => ({
  key: i,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  size: `${1 + Math.random() * 2}px`,
  opacity: 0.3 + Math.random() * 0.5,
}));

function LoadingScreen({ message }: { progress: number; message: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0f1b26',
        fontFamily: "'JetBrains Mono', monospace",
        color: '#f5e8d1',
        zIndex: 1000,
      }}
    >
      {/* Starfield */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', pointerEvents: 'none' }}>
        {loadingStars.map((s) => (
          <div
            key={s.key}
            style={{
              position: 'absolute',
              left: s.left,
              top: s.top,
              width: s.size,
              height: s.size,
              backgroundColor: '#f5e8d1',
              opacity: s.opacity,
              borderRadius: '50%',
            }}
          />
        ))}
      </div>

      {/* Title */}
      <h1
        style={{
          fontSize: 72,
          color: '#dd5639',
          textShadow: '0 0 10px #20a5a6',
          letterSpacing: '0.3em',
          marginBottom: 8,
        }}
      >
        ASTRA
      </h1>
      <div style={{ fontSize: 11, color: '#4a5a6a', marginBottom: 40 }}>SPACE WONDERING</div>

      {/* Spinner */}
      <ScaleLoader color="#20a5a6" height={35} width={4} radius={2} margin={3} />

      {/* Status message */}
      <p style={{ marginTop: 24, color: '#4a5a6a', fontSize: 11 }}>{message}</p>
    </div>
  );
}

export function App() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const sceneManagerRef = useRef<SceneManager | null>(null);
  const resizeHandlerRef = useRef<(() => void) | null>(null);

  const [appState, setAppState] = useState<AppState>('login');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [viewMode, setViewMode] = useState<'flight' | 'constellation' | 'universe'>('flight');
  const [isDocked, setIsDocked] = useState(false);
  const [stationId, setStationId] = useState('');
  const [stationName, setStationName] = useState('');
  const [gameInitialized, setGameInitialized] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false); // Chat starts minimized
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [bountyBoardOpen, setBountyBoardOpen] = useState(false);
  const [mapView, setMapView] = useState<'none' | 'constellation' | 'universe'>('none');
  const [isInNebula, setIsInNebula] = useState(false);

  // Refs to track current state for keyboard handler (avoids stale closures)
  const inventoryOpenRef = useRef(inventoryOpen);
  const bountyBoardOpenRef = useRef(bountyBoardOpen);
  const mapViewRef = useRef(mapView);
  const isDockedRef = useRef(isDocked);
  inventoryOpenRef.current = inventoryOpen;
  bountyBoardOpenRef.current = bountyBoardOpen;
  mapViewRef.current = mapView;
  isDockedRef.current = isDocked;
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsOpenRef = useRef(settingsOpen);
  settingsOpenRef.current = settingsOpen;
  const [constellationMapData, setConstellationMapData] = useState<{
    constellationName: string;
    constellationSize: number;
    playerPosition: { x: number; y: number };
    playerRotation: number;
    planets: Array<{ id: string; name: string; systemId: string; x: number; y: number; radius: number; planetType: string; orbitalDistance?: number; hasPlayerOutpost?: boolean }>;
    stations: Array<{ id: string; name: string; x: number; y: number }>;
    asteroidZones?: Array<{ id: string; x: number; y: number; radius: number; name?: string }>;
    gateways: Array<{ id: string; name: string; x: number; y: number; targetConstellationName: string }>;
    systems: Array<{ id: string; name: string; x: number; y: number; starType: string; sunRadius: number }>;
    privateStations?: Array<{ id: string; name: string; x: number; y: number; isOwned: boolean }>;
    waypoint?: { x: number; y: number; name: string } | null;
    harvestables?: Array<{ id: string; kind: 'ice_asteroid' | 'gas_pocket' | 'wreckage'; x: number; y: number; resourceType: string }>;
    tutorialBeacons?: Array<{ id: string; name: string; x: number; y: number; radius: number }>;
  } | null>(null);

  // Death state (Green/Yellow zones - ship kept)
  const [deathData, setDeathData] = useState<{
    killerName?: string;
    killerType?: 'player' | 'bot' | 'environment';
    nearestStationName: string;
    nearestStationId: string;
    threatLevel: 'green' | 'yellow' | 'red';
    itemsDropped: string[];
  } | null>(null);

  // Ship lost state (Red zone - ship deleted)
  const [shipLostData, setShipLostData] = useState<{
    lostShipClass: string;
    nearestStationName: string;
    nearestStationId: string;
    itemsDropped: string[];
  } | null>(null);

  // Initialize PixiJS application when user clicks Play
  const startGame = async () => {
    if (gameInitialized || !canvasRef.current) return;

    setAppState('loading');

    // Create application
    const app = new Application({
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: 0x0f1b26,
      resolution: window.devicePixelRatio,
      autoDensity: true,
    });

    await app.init();
    appRef.current = app;

    // Append canvas to container
    canvasRef.current.appendChild(app.canvas);

    // Set up renderer for asset loader
    assetLoader.setRenderer(app.renderer);

    // Create input manager and set canvas
    const input = new InputManager();
    input.setCanvas(app.canvas);

    // Create camera with app and input
    // Note: worldContainer will be set by GameScene when it creates its world
    const camera = new Camera(app, input);

    // Create scene manager
    const sceneManager = new SceneManager(app, input, camera);
    sceneManagerRef.current = sceneManager;

    // Register scenes
    sceneManager.register('BootScene', BootScene);
    sceneManager.register('GameScene', GameScene);

    // Handle resize
    const handleResize = () => {
      app.resize(window.innerWidth, window.innerHeight);
    };
    resizeHandlerRef.current = handleResize;
    window.addEventListener('resize', handleResize);

    // Start with BootScene
    await sceneManager.start('BootScene');

    setGameInitialized(true);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      if (sceneManagerRef.current) {
        sceneManagerRef.current.destroy();
      }
      if (appRef.current) {
        appRef.current.destroy();
      }
    };
  }, []);

  // Subscribe to game events
  useEffect(() => {
    const unsubProgress = onGameEvent('loadingProgress', ({ progress, message }) => {
      setLoadingProgress(progress);
      setLoadingMessage(message);
    });

    const unsubReady = onGameEvent('gameReady', () => {
      setAppState('playing');
    });

    const unsubViewMode = onGameEvent('viewModeChange', (mode) => {
      setViewMode(mode);
    });

    const unsubDocking = onGameEvent('dockingStateChange', ({ isDocked: docked, stationId: id, stationName: name }) => {
      setIsDocked(docked);
      if (id) setStationId(id);
      if (name) setStationName(name);
    });

    const unsubNebula = onGameEvent('nebulaStateChange', (data) => {
      setIsInNebula(data.isInNebula);
    });

    const unsubInventory = onGameEvent('toggleInventory', () => {
      setInventoryOpen((prev) => !prev);
    });

    const unsubChat = onGameEvent('toggleChat', () => {
      setChatOpen((prev) => !prev);
    });

    const unsubEquipment = onGameEvent('toggleEquipment', () => {
      setEquipmentOpen((prev) => !prev);
    });

    const unsubBountyBoard = onGameEvent('toggleBountyBoard', () => {
      setBountyBoardOpen((prev) => !prev);
    });

    // Handle bounty claimed notification
    const unsubBountyClaimed = onGameEvent('bountyClaimed', (data) => {
      toast.success(`🎯 BOUNTY CLAIMED: ${data.amount.toLocaleString()} CR (+${data.repGain} rep)`, {
        icon: false,
        autoClose: 4000,
      });
    });

    const unsubOpenConstellationMap = onGameEvent('openConstellationMap', (data) => {
      // Store map data for the overlay component
      setConstellationMapData(data);
      setMapView('constellation');
    });

    const unsubPlayerDied = onGameEvent('playerDied', (data) => {
      // Delay modal so the explosion animation plays first
      setTimeout(() => setDeathData(data), 1500);
    });

    const unsubShipLost = onGameEvent('shipLost', (data) => {
      setShipLostData(data);
    });

    const unsubPlayerRespawned = onGameEvent('playerRespawned', () => {
      setDeathData(null);
      setShipLostData(null);
    });

    const unsubLootPickup = onGameEvent('lootPickup', (data) => {
      // Build toast message
      const parts: string[] = [];
      if (data.credits > 0) {
        parts.push(`+${data.credits} credits`);
      }
      if (data.items && data.items.length > 0) {
        parts.push(...data.items);
      }
      if (parts.length > 0) {
        toast.success(parts.join(', '), {
          icon: false,
          autoClose: 2000,
        });
      }
    });

    // Keyboard shortcuts for panels and map
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'escape':
          // ESC closes settings first, then any open panel, then undocks if docked, then opens menu
          if (settingsOpenRef.current) {
            setSettingsOpen(false);
          } else if (inventoryOpenRef.current) {
            setInventoryOpen(false);
          } else if (mapViewRef.current !== 'none') {
            setMapView('none');
          } else if (isDockedRef.current) {
            emitGameEvent('undock');
          } else {
            setMenuOpen((prev) => !prev);
          }
          break;
        case 'm':
          setMapView((prev) => {
            if (prev === 'none') {
              // Request fresh map data from GameScene before opening
              emitGameEvent('requestConstellationMapData');
              return 'constellation';
            }
            return 'none';
          });
          break;
        case 'i':
          e.preventDefault();
          setInventoryOpen((prev) => {
            const next = !prev;
            // Tutorial: server unlocks Continue on the "inventory" step.
            // (tutorial v2 removed the inventory trigger — Continue is manual)
            return next;
          });
          break;
        case 'c':
          setChatOpen((prev) => !prev);
          break;
        case 'b':
          // Don't open bounty board while docked (station has its own bounty UI)
          if (!isDockedRef.current) {
            setBountyBoardOpen((prev) => !prev);
          }
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      unsubProgress();
      unsubReady();
      unsubViewMode();
      unsubDocking();
      unsubNebula();
      unsubInventory();
      unsubChat();
      unsubEquipment();
      unsubBountyBoard();
      unsubBountyClaimed();
      unsubOpenConstellationMap();
      unsubPlayerDied();
      unsubShipLost();
      unsubPlayerRespawned();
      unsubLootPickup();
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Empty deps - handler uses refs for current state

  return (
    <div style={containerStyle}>
      {/* Login Screen */}
      {appState === 'login' && <LoginForm onPlay={startGame} />}

      {/* PixiJS Canvas Container (hidden until game starts) */}
      <div
        ref={canvasRef}
        style={{
          ...canvasContainerStyle,
          display: appState === 'login' ? 'none' : 'block',
        }}
      />

      {/* Loading Screen */}
      {appState === 'loading' && (
        <LoadingScreen progress={loadingProgress} message={loadingMessage} />
      )}

      {/* Nebula fog vignette overlay — always mounted, fades in/out */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        boxShadow: 'inset 0 0 300px 200px rgba(10, 8, 30, 0.6)',
        transition: 'opacity 2s ease-in-out',
        opacity: isInNebula ? 1 : 0,
        zIndex: 5,
      }} />

      {/* UI Overlay (React components go here) */}
      {appState === 'playing' && (
        <div style={{ ...uiOverlayStyle, pointerEvents: isDocked || mapView !== 'none' || deathData || shipLostData ? 'auto' : 'none' }}>
          {/* Tutorial transmission panel — server-pushed; renders only when a
              tutorialState event arrives. Mounted at top level so it stays
              visible across flight / station / map / death overlays. The panel
              has pointer-events:auto on its own root so it stays clickable even
              when the parent overlay has pointer-events:none. */}
          <TutorialTransmissionPanel />
          <WelcomeTransmissionPanel />
          {/* HUD shown in flight mode - keep mounted but hidden when maps open to preserve scanned entities */}
          {viewMode === 'flight' && !isDocked && (
            <div style={{ display: mapView === 'none' ? 'block' : 'none' }}>
              <GameHUD />
            </div>
          )}
          {/* Constellation Map Overlay */}
          {mapView === 'constellation' && (
            <ConstellationMapOverlay
              onClose={() => setMapView('none')}
              onZoomToUniverse={() => setMapView('universe')}
              mapData={constellationMapData}
            />
          )}
          {/* Universe Map Overlay */}
          {mapView === 'universe' && (
            <UniverseMapOverlay
              onClose={() => setMapView('none')}
              onZoomToConstellation={() => setMapView('constellation')}
            />
          )}
          {/* Station UI when docked */}
          {isDocked && (
            <StationUI
              stationId={stationId}
              stationName={stationName}
              onUndock={() => {
                emitGameEvent('undock');
              }}
            />
          )}
          {/* Ship Inventory Modal (toggleable with I key - combines equipment + cargo) */}
          <ShipInventoryModal isOpen={inventoryOpen} onClose={() => setInventoryOpen(false)} />
          {/* Bounty Board Modal (toggleable with B key - hidden when docked) */}
          {!isDocked && <BountyBoardModal isOpen={bountyBoardOpen} onClose={() => setBountyBoardOpen(false)} />}
          {/* Chat panel (toggleable with C key) - hidden when docked since station has its own chat */}
          {!isDocked && <ChatPanel isOpen={chatOpen} onClose={() => setChatOpen((prev) => !prev)} />}
          {/* Main menu (ESC key) */}
          {menuOpen && !settingsOpen && (
            <MainMenu
              onClose={() => setMenuOpen(false)}
              onSettings={() => setSettingsOpen(true)}
              onQuit={() => {
                setMenuOpen(false);
                setAppState('login');
                setGameInitialized(false);
                // Clean up game resources
                if (sceneManagerRef.current) {
                  sceneManagerRef.current.destroy();
                  sceneManagerRef.current = null;
                }
                if (appRef.current) {
                  appRef.current.destroy();
                  appRef.current = null;
                }
                // Clear canvas container
                if (canvasRef.current) {
                  canvasRef.current.innerHTML = '';
                }
              }}
            />
          )}
          {/* Settings screen */}
          {settingsOpen && (
            <SettingsScreen onBack={() => setSettingsOpen(false)} />
          )}
          {/* Death modal (Green/Yellow zones) */}
          {deathData && (
            <DeathModal
              killerName={deathData.killerName}
              killerType={deathData.killerType}
              nearestStationName={deathData.nearestStationName}
              threatLevel={deathData.threatLevel}
              itemsDropped={deathData.itemsDropped}
              onRespawn={() => setDeathData(null)}
            />
          )}
          {/* Planet management panel — opens via E near a planet. Renders
              on top of the existing GameScene; the ship stays in space. */}
          <PlanetPanel />
          {/* Ship lost modal (Red zone - ship deleted) */}
          {shipLostData && (
            <DeathModal
              nearestStationName={shipLostData.nearestStationName}
              isShipLost={true}
              lostShipClass={shipLostData.lostShipClass}
              itemsDropped={shipLostData.itemsDropped}
              onRespawn={() => setShipLostData(null)}
            />
          )}
        </div>
      )}

      {/* Toast notifications - bottom right */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover
        theme="dark"
        toastStyle={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '11px',
          backgroundColor: '#1a2a38',
          border: '1px solid #2a3a48',
          borderRadius: '4px',
        }}
      />
    </div>
  );
}

export default App;
